import { useCallback, useMemo, useState } from 'react';

import { MedicalDeviceOrderError, MedicalDeviceType } from '@dreryk/egabinet-ezwm-frontend-api';
import { Disease } from '@dreryk/medical-dictionaries-api';

import { PromiseVoidFunction, useFormController } from '@dreryk/react-common';
import {
   appendTestableElementAttributes,
   Notification,
   NotificationSize,
   NotificationType,
} from '@dreryk/react-components';

import { medicalDevicesTranslationKeys } from '../translations';

import { AdditionalInformationField } from './fields/AdditionalInformationField';
import { AdditionalIndicationsSection } from './fields/AdditionalndicationsSection/AdditionalIndicationsSection';
import { DeviceAwardCriteriaField } from './fields/DeviceAwardCriteria/DeviceAwardCriteriaField';
import { DiagnosisField } from './fields/DiagnosisField';
import { PlacementField } from './fields/PlacementField';
import { MedicalDeviceFormConfigurationSection } from './MedicalDeviceFormConfigurationSection/MedicalDeviceFormConfigurationSection';
import { MedicalDeviceFormDeviceSupplySection } from './MedicalDeviceFormDeviceSupplySection/MedicalDeviceFormDeviceSupplySection';
import { MedicalDeviceErrorsActionType, useErrorsReducer } from './medicalDeviceOrder.hooks';
import {
   MedicalDeviceAllFieldsFormController,
   MedicalDeviceOrderAdditionalIndicationsFormBeanPart,
   MedicalDeviceOrderAllFieldsFormBean,
   MedicalDeviceOrderFormBean,
   MedicalDeviceOrderFormWatch,
   PatientAdditionalEntitlementCode,
} from './medicalDeviceOrder.types';
import {
   isMedicalDeviceFieldAvailable,
   shouldDisplayAdditionalIndicationsSection,
   shouldDisplayDeviceAwardCriteriaTable,
   shouldDisplayPlacementField,
} from './medicalDeviceOrder.utils';
import { MedicalDeviceOrderDocumentVerificationErrorsList } from './MedicalDeviceOrderDocumentVerificationErrorsList';
import {
   MedicalDeviceOrderEzwmAuthenticationFault,
   MedicalDeviceOrderEzwmLoginErrorsModal,
} from './MedicalDeviceOrderEzwmLoginErrorsModal';
import {
   StyledMedicalDeviceOrderDocumentVerificationErrorsSection,
   StyledMedicalDeviceOrderForm,
   StyledMedicalDeviceOrderFormFieldsSection,
} from './MedicalDeviceOrderForm.styled';
import { MedicalDeviceOrderFormRestorer } from './MedicalDeviceOrderFormRestorer';
import { MedicalDeviceOrderPrintAction } from './MedicalDeviceOrderPrintAction';
import { MedicalDeviceOrderSubmitAction } from './MedicalDeviceOrderSubmitAction';
import { MedicalDeviceDraftRouteLeavingGuard } from './MedicalDeviceRouteLeavingGuard';

type MedicalDeviceOrderFormProps = {
   readonly shouldDisplayPregnantField: boolean;
   readonly visitDisease?: Disease;
   readonly visitId: number;
   readonly draftValues?: Partial<MedicalDeviceOrderAllFieldsFormBean>;
   readonly documentVerificationErrors: MedicalDeviceOrderError[];
   readonly onDocumentVerificationErrors: PromiseVoidFunction;
   readonly printable: boolean | undefined;
   readonly existingOrderId: number | undefined;
   readonly forChildren: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const MedicalDeviceOrderForm = (props: MedicalDeviceOrderFormProps): JSX.Element => {
   const [printableWithoutVerification, setPrintableWithoutVerification] = useState<boolean>(props.printable !== false);
   const [errorsState, dispatchErrorsStateAction] = useErrorsReducer(props.documentVerificationErrors);
   const defaultValues = useMemo(
      () => ({
         deviceType: 'OTHER' as MedicalDeviceType,
         device: null,
         disease: props.visitDisease ?? null,
         deviceAwardCriteria: [],
         additionalInformation: null,
         // TODO DREGAB-4872 To be done as part of whole epic
         additionalEntitlementCode: '47IB' as PatientAdditionalEntitlementCode,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
   );
   const initialValues: Partial<MedicalDeviceOrderFormBean> = useMemo(
      () => props.draftValues ?? defaultValues,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
   );
   const {
      register,
      setValue,
      watch,
      getValues,
      handleSubmit,
      handleSubmitDraft,
      formState,
      reset,
      trigger,
   } = useFormController<MedicalDeviceOrderFormBean>(initialValues);
   const handleAllFieldsSubmit = handleSubmit as MedicalDeviceAllFieldsFormController['handleSubmit'];
   const device = watch('device');

   const setEzwmLoginError = useCallback(
      (error: MedicalDeviceOrderEzwmAuthenticationFault | null) => {
         dispatchErrorsStateAction({
            type: MedicalDeviceErrorsActionType.SET_LOGIN_ERROR,
            ezwmAuthenticationFault: error,
         });
      },
      [dispatchErrorsStateAction]
   );
   const { onDocumentVerificationErrors } = props;
   const handleDocumentVerificationErrors = useCallback(
      async (errors: MedicalDeviceOrderError[]) => {
         reset(getValues());
         dispatchErrorsStateAction({
            type: MedicalDeviceErrorsActionType.SET_DOCUMENT_VERIFICATION_ERRORS,
            documentVerificationErrors: errors,
         });
         setPrintableWithoutVerification(false);
         await onDocumentVerificationErrors();
      },
      [dispatchErrorsStateAction, getValues, reset, onDocumentVerificationErrors]
   );
   const handleVerificationErrorsModalClose = useCallback(() => {
      setEzwmLoginError(null);
   }, [setEzwmLoginError]);

   return (
      <StyledMedicalDeviceOrderForm noValidate>
         <StyledMedicalDeviceOrderFormFieldsSection direction={'column'} gap={'24px'}>
            <MedicalDeviceOrderFormRestorer defaultValues={defaultValues} reset={reset} watch={watch} />

            <MedicalDeviceFormConfigurationSection register={register} watch={watch} />

            {shouldDisplayPlacementField(register, isMedicalDeviceFieldAvailable(watch, 'placement')) ? (
               <PlacementField register={register} watch={watch} />
            ) : null}

            {device ? (
               <MedicalDeviceFormDeviceSupplySection
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  formState={formState}
                  trigger={trigger}
               />
            ) : null}

            <DiagnosisField register={register} name={'disease'} />

            {shouldDisplayDeviceAwardCriteriaTable(register, Boolean(watch('device'))) ? (
               <DeviceAwardCriteriaField register={register} watch={watch} setValue={setValue} />
            ) : null}

            <AdditionalInformationField register={register} />

            {shouldDisplayAdditionalIndicationsSection(
               register,
               isMedicalDeviceFieldAvailable(watch, 'additionalIndications')
            ) ? (
               <AdditionalIndicationsSection
                  register={register}
                  watch={watch as MedicalDeviceOrderFormWatch<MedicalDeviceOrderAdditionalIndicationsFormBeanPart>}
                  showReasonField={props.forChildren}
               />
            ) : null}

            <MedicalDeviceOrderPrintAction
               disabled={!printableWithoutVerification}
               handleSubmit={handleAllFieldsSubmit}
               onSuccess={reset}
            />

            <MedicalDeviceOrderSubmitAction
               handleSubmit={handleAllFieldsSubmit}
               trigger={trigger}
               onEzwmLoginError={setEzwmLoginError}
               onDocumentVerificationErrors={handleDocumentVerificationErrors}
               onSuccess={reset}
               printableWithoutVerification={printableWithoutVerification}
            />

            <MedicalDeviceDraftRouteLeavingGuard
               formState={formState}
               handleSubmitDraft={handleSubmitDraft as MedicalDeviceAllFieldsFormController['handleSubmitDraft']}
            />
         </StyledMedicalDeviceOrderFormFieldsSection>

         {errorsState.documentVerificationErrors.length > 0 ? (
            <StyledMedicalDeviceOrderDocumentVerificationErrorsSection
               direction={'column'}
               gap={'10px'}
               {...appendTestableElementAttributes('documentVerificationErrors')}
            >
               <Notification
                  testId={'documentVerificationErrors'}
                  label={medicalDevicesTranslationKeys.documentVerificationIssues}
                  type={NotificationType.ERROR}
                  size={NotificationSize.SMALL}
               />
               <MedicalDeviceOrderDocumentVerificationErrorsList errors={errorsState.documentVerificationErrors} />
            </StyledMedicalDeviceOrderDocumentVerificationErrorsSection>
         ) : null}

         {errorsState.ezwmAuthenticationFault && printableWithoutVerification === true ? (
            <MedicalDeviceOrderEzwmLoginErrorsModal
               onClose={handleVerificationErrorsModalClose}
               handleSubmit={handleSubmit as MedicalDeviceAllFieldsFormController['handleSubmit']}
               fault={errorsState.ezwmAuthenticationFault}
               onSuccess={reset}
               printableWithoutVerification
            />
         ) : null}
         {errorsState.ezwmAuthenticationFault && printableWithoutVerification === false ? (
            <MedicalDeviceOrderEzwmLoginErrorsModal
               onClose={handleVerificationErrorsModalClose}
               fault={errorsState.ezwmAuthenticationFault}
               printableWithoutVerification={false}
            />
         ) : null}
      </StyledMedicalDeviceOrderForm>
   );
};
